import {
  Spinner,
  Button,
  IconPlus,
  MagicTable,
  MagicTableCell,
  MagicTableCellEmpty,
  type MagicTableColumnDef,
  MagicTableCellInvoiceStatus,
  MagicTableEmptyState,
  MagicTableFilterEmptyState,
  SelectAllPrompt,
  IconReceiptBill,
  ActiveFilter,
  Center,
  Box,
  Flex
} from '@sequencehq/core-components'
import { toMoney } from '@sequencehq/utils'
import { localDateWithFormat } from '@sequencehq/utils'
import { CurrentUserId } from 'components/CurrentUserId/CurrentUserId'
import { InvoicesViewSettingsHeader } from 'components/Invoices/InvoicesViewSettingsHeader'
import { FC, useCallback, useMemo, useState } from 'react'
import { InvoicesSegmentedFilters } from 'components/Invoices/InvoicesSegmentedFilters'
import { TableLinksLogos } from 'modules/Integrations'
import { formatDateRange, formatDistanceToNow } from '@sequencehq/utils/dates'
import { Dashboardv99990101Api } from '@sequencehq/api/dashboard/v99990101'
import { InvoicesFilters, InvoicesPropFilters } from '../types'
import { CustomerPreviewCardPill } from 'Customer/components/CustomerPreviewCard'
import { BulkActionsInFlight } from 'modules/BulkActions'
import { useInvoicesMagicTable } from './useInvoicesMagicTable'
import { useNavigate } from 'react-router-dom'
import { useIsOnSavedViewPage } from 'SavedViews/savedViews.router'
import { InvoiceBulkActions } from '../InvoiceBulkActions'
import { invoiceBulkActionConfig } from './invoiceBulkAction.config'
import { invoicePaths } from 'modules/Invoices'
import { ProtectedButton, usePermissions } from 'lib/rbac'
import { InvoiceKebabMenu } from './InvoiceKebabMenu'
import { INVOICES_DEFAULT_SORT_BY } from 'components/Invoices/invoices.constants'
import { useInvoiceDueDateFormatter } from 'Invoices/hooks/useInvoiceDueDateFormatter'
import {
  toAmountBasis,
  toListedInvoiceAmount,
  toListedInvoiceOutstanding
} from 'Invoices/utils'
import { useFeatureFlags } from 'hooks/useFeatureFlags'

type Invoice = Dashboardv99990101Api.GetInvoicesListSummary.InvoiceListSummary
type InvoicesView = {
  model: Invoice
  controlFilters: InvoicesFilters
  propFilters: InvoicesPropFilters
}

type Props = {
  onSaveFilters?: (activeFilters: ActiveFilter[], sortBy: string | null) => void
  onUpdateSavedFilters?: (
    activeFilters: ActiveFilter[],
    sortBy: string | null
  ) => void
  showSegmentedFilters?: boolean
  forcedActiveFilters?: ActiveFilter[]
  onFiltersApiQueryParamsChanged: (newParams: string) => void
  lockedControls: boolean
}

export const InvoicesMagicTable: FC<Props> = ({
  onSaveFilters,
  onUpdateSavedFilters,
  showSegmentedFilters = false,
  forcedActiveFilters = [],
  onFiltersApiQueryParamsChanged,
  lockedControls
}) => {
  const navigate = useNavigate()
  const isSavedView = useIsOnSavedViewPage()
  const { useCreditGrantLedger } = useFeatureFlags()
  const amountBasis = toAmountBasis(useCreditGrantLedger)
  const formatDueDate = useInvoiceDueDateFormatter()
  const [disableBulkActionCheckboxes, setDisableBulkActionCheckboxes] =
    useState<boolean>(false)

  const [showBulkActionsInFlight, setShowBulkActionsInFlight] = useState(false)

  const {
    isLoading,
    filters,
    activeFilters,
    sortBy,
    onChangeActiveFilters,
    onChangeSortBy,
    selectedInvoices,
    setSelectedInvoices,
    infiniteQuery,
    selectAll
  } = useInvoicesMagicTable({
    forcedActiveFilters,
    onFiltersApiQueryParamsChanged
  })

  const handleChangeActiveBulkActionCount = useCallback(
    (bulkActionCount: number) => {
      setDisableBulkActionCheckboxes(bulkActionCount > 0)
    },
    []
  )

  const handleChangeHasBulkActions = useCallback((hasBulkActions: boolean) => {
    setShowBulkActionsInFlight(hasBulkActions)
  }, [])

  const creditGrantColumns: MagicTableColumnDef<Invoice>[] =
    useCreditGrantLedger
      ? [
          {
            // Off by default: with no credit note or payment, this repeats Amount.
            id: 'balance',
            header: 'Outstanding',
            accessorFn: row => row.balance,
            cell: ({ row, column }) => (
              <MagicTableCell
                data-testid={`invoices.outstanding.${row.original.id}`}
                text={toListedInvoiceOutstanding(row.original)}
                textAlign={column.columnDef?.meta?.textAlign}
              />
            ),
            hiddenByDefault: true,
            meta: { textAlign: 'right' }
          },
          {
            id: 'originalGrossTotal',
            header: 'Gross total',
            accessorFn: row => row.grossTotal,
            cell: ({ row, column }) => (
              <MagicTableCell
                text={toMoney({
                  currency: row.original.currency,
                  value: row.original.grossTotal
                })}
                textAlign={column.columnDef?.meta?.textAlign}
              />
            ),
            // Keep the grossTotal sort key compatible with saved views and links.
            enableSorting: true,
            sortingConfiguration: { sortProperty: 'grossTotal' },
            hiddenByDefault: true,
            meta: { textAlign: 'right' }
          }
        ]
      : []

  // Show the value used by a restored sort, even if its column was hidden.
  const requiredColumnIds = useMemo(
    () =>
      useCreditGrantLedger && sortBy?.startsWith('grossTotal')
        ? ['originalGrossTotal']
        : [],
    [useCreditGrantLedger, sortBy]
  )

  const { can } = usePermissions()
  // Check if they can access bulk actions AND any of the actions of the resource they're trying to do in bulk
  const canUseInvoiceBulkActions =
    can('org.bulkAction:create') &&
    (can('org.invoice:update') ||
      can('org.invoice:delete') ||
      can('org.invoice:create'))

  if (isLoading) {
    return (
      <Center height="100%">
        <Spinner />
      </Center>
    )
  }

  const additionalToolbarComponentsV3 = showSegmentedFilters
    ? {
        additionalToolbarComponentsV3: {
          start: <InvoicesSegmentedFilters />
        }
      }
    : {}

  return (
    <Box height="100%" width="100%" data-testid="invoices.magicTable">
      <CurrentUserId>
        {userId => (
          <>
            <MagicTable<InvoicesView>
              additionalTableHeaderRow={({ visibleTableWidth }) => {
                if (!canUseInvoiceBulkActions || !selectAll.showPrompt) {
                  return null
                }

                return (
                  <SelectAllPrompt
                    entityName="invoices"
                    onSelectAll={selectAll.onSelectAll}
                    showClearSelection={selectAll.showClearSelection}
                    onClearSelection={selectAll.onClearSelection}
                    visibleTableWidth={visibleTableWidth}
                    loading={selectAll.loading}
                    total={selectAll.total}
                    totalSelected={selectAll.totalSelected}
                  />
                )
              }}
              disableCheckboxes={
                !canUseInvoiceBulkActions ||
                disableBulkActionCheckboxes ||
                selectAll.loading
              }
              activeFilters={activeFilters}
              onChangeActiveFilters={onChangeActiveFilters}
              sortBy={sortBy}
              onChangeSortBy={onChangeSortBy}
              defaultSortBy={INVOICES_DEFAULT_SORT_BY}
              entityNamePlural="invoices"
              sequenceUserId={userId}
              infiniteQuery={infiniteQuery}
              rowPath={(row: InvoicesView['model']) => `/invoices/${row.id}`}
              columns={[
                {
                  id: 'grossTotal',
                  header: 'Amount',
                  accessorFn: row => row.grossTotal,
                  cell: ({ row, column }) => (
                    <MagicTableCell
                      data-testid={`invoices.amount.${row.original.id}`}
                      text={toListedInvoiceAmount({
                        invoice: row.original,
                        basis: amountBasis
                      })}
                      textAlign={column.columnDef?.meta?.textAlign}
                    />
                  ),
                  // Credit-adjusted sorting requires API support (PULSE-1975).
                  enableSorting: !useCreditGrantLedger,
                  meta: { textAlign: 'right' }
                },
                ...creditGrantColumns,
                {
                  id: 'currency',
                  header: 'Currency',
                  accessorKey: 'currency',
                  cell: value => {
                    const currency = value.getValue<Invoice['currency']>()

                    return <MagicTableCell text={currency} />
                  }
                },
                {
                  id: 'status',
                  header: 'Status',
                  accessorFn: row => ({
                    invoiceStatus: row.status,
                    paymentStatus: row.paymentStatus
                  }),
                  cell: value => {
                    const { invoiceStatus, paymentStatus } = value.getValue<{
                      invoiceStatus: Invoice['status']
                      paymentStatus: Invoice['paymentStatus']
                    }>()

                    return (
                      <MagicTableCellInvoiceStatus
                        invoiceStatus={invoiceStatus}
                        paymentStatus={paymentStatus}
                      />
                    )
                  }
                },
                {
                  id: 'invoiceNumber',
                  header: 'Invoice #',
                  accessorKey: 'invoiceNumber',
                  cell: value => {
                    const invoiceNumber =
                      value.getValue<Invoice['invoiceNumber']>()

                    if (!invoiceNumber) {
                      return null
                    }

                    return (
                      <MagicTableCell
                        data-testid={`invoices.invoiceNumber.${value.row.original.id}`}
                        text={invoiceNumber}
                      />
                    )
                  }
                },
                {
                  id: 'customerLegalCompanyName',
                  header: 'Customer',
                  accessorFn: row => ({
                    customerId: row.customerId,
                    customerLegalCompanyName: row.customerLegalCompanyName
                  }),
                  cell: value => {
                    const { customerId, customerLegalCompanyName } =
                      value.getValue<{
                        customerId: Invoice['customerId']
                        customerLegalCompanyName: Invoice['customerLegalCompanyName']
                      }>()

                    return (
                      <Flex
                        alignItems="center"
                        justifyContent="space-between"
                        height="40px"
                        pl="6px"
                      >
                        <CustomerPreviewCardPill
                          customerId={customerId}
                          variant="ghost"
                          alternativeName={customerLegalCompanyName}
                        />
                      </Flex>
                    )
                  },
                  enableSorting: true
                },
                {
                  id: 'billingPeriod',
                  header: 'Billing Period',
                  accessorFn: row => ({
                    billingPeriod: row.billingPeriod
                  }),
                  cell: value => {
                    const { billingPeriod } = value.getValue<{
                      billingPeriod: Invoice['billingPeriod']
                    }>()

                    return (
                      <MagicTableCell
                        text={
                          billingPeriod
                            ? formatDateRange({
                                from: new Date(billingPeriod.start),
                                to: new Date(billingPeriod.endInclusive)
                              })
                            : ''
                        }
                      />
                    )
                  }
                },
                {
                  id: 'issueDate',
                  header: 'Sent',
                  accessorKey: 'issueDate',
                  cell: value => (
                    <MagicTableCell
                      text={
                        localDateWithFormat(
                          value.getValue<Invoice['issueDate']>(),
                          'd MMM yyyy'
                        ) || ''
                      }
                    />
                  ),
                  enableSorting: true
                },
                {
                  id: 'dueDate',
                  header: 'Due',
                  accessorKey: 'dueDate',
                  cell: value => (
                    <MagicTableCell
                      data-testid={`invoices.dueDate.${value.row.original.id}`}
                      text={formatDueDate({
                        dueDate: value.getValue<Invoice['dueDate']>(),
                        formatDate: dueDate =>
                          localDateWithFormat(dueDate, 'd MMM yyyy')
                      })}
                    />
                  ),
                  enableSorting: true
                },
                {
                  id: 'accountingDate',
                  header: 'Date',
                  accessorKey: 'accountingDate',
                  cell: value => (
                    <MagicTableCell
                      text={
                        localDateWithFormat(
                          value.getValue<Invoice['accountingDate']>(),
                          'd MMM yyyy'
                        ) || ''
                      }
                    />
                  ),
                  enableSorting: true
                },
                {
                  id: 'lastRecalculated',
                  header: 'Last Recalculated',
                  accessorKey: 'calculatedAt',
                  cell: row => {
                    const calculatedAt = row.getValue<Invoice['calculatedAt']>()

                    return (
                      <MagicTableCell
                        text={
                          calculatedAt
                            ? formatDistanceToNow(new Date(calculatedAt), {
                                addSuffix: true
                              })
                            : ''
                        }
                      />
                    )
                  }
                },
                {
                  // The column id stays 'linkedServices' so existing saved-view
                  // column-visibility settings keep working; only the data
                  // field moved to 'links' on the slim endpoint.
                  id: 'linkedServices',
                  header: 'Links',
                  accessorKey: 'links',
                  cell: row => {
                    const links = row.getValue<Invoice['links']>()

                    if (!links.length) {
                      return null
                    }

                    return (
                      <MagicTableCellEmpty>
                        <TableLinksLogos
                          services={links.map(l => l.externalService)}
                        />
                      </MagicTableCellEmpty>
                    )
                  }
                }
              ]}
              kebabMenu={(model: Invoice) => (
                <InvoiceKebabMenu
                  invoiceId={model.id}
                  linkedServices={model.links}
                  invoiceNumber={model.invoiceNumber}
                  invoiceStatus={model.status}
                  paymentStatus={model.paymentStatus}
                />
              )}
              filters={filters}
              emptyStateUnfiltered={entityNamePlural => (
                <MagicTableEmptyState
                  data-testid="invoices.emptyState"
                  entityNamePlural={entityNamePlural}
                  imageSrc="/img/empty/invoices.png"
                  learnMoreText="Auto-invoice customers with billing schedules or create a one-off invoice"
                  learnMoreHref="https://docs.sequencehq.com/invoicing/invoicing-customers"
                  buttonNew={
                    <ProtectedButton permission="org.invoice:create">
                      <Button
                        onClick={() => {
                          navigate(invoicePaths.new())
                        }}
                        variant="primary"
                        leadingIcon={<IconPlus color="inherit" />}
                      >
                        New invoice
                      </Button>
                    </ProtectedButton>
                  }
                />
              )}
              emptyStateFiltered={(entityNamePlural, clearFilters) => (
                <MagicTableFilterEmptyState
                  entityNamePlural={entityNamePlural}
                  entityIcon={IconReceiptBill}
                  actionClearFilters={clearFilters}
                  actionViewAll={() => navigate('/invoices')}
                  variant={isSavedView ? 'SAVED_VIEW' : 'TABLE'}
                />
              )}
              bulkActions={
                canUseInvoiceBulkActions
                  ? {
                      selectedItems: selectedInvoices,
                      onSelectedItemsChange: setSelectedInvoices,
                      bar: (
                        selectedRows: Invoice[],
                        clearSelectedRows: () => void
                      ) => {
                        if (selectAll.loading) {
                          return undefined
                        }

                        const bulkActionBar = (
                          <InvoiceBulkActions
                            selectedRows={selectedRows}
                            clearSelectedRows={clearSelectedRows}
                            popoverOffset={[0, 0]}
                          >
                            <Box
                              position="absolute"
                              top="50%"
                              left="50%"
                              transform="translate(-50%, -50%)"
                            />
                          </InvoiceBulkActions>
                        )

                        if (!showBulkActionsInFlight) {
                          return bulkActionBar
                        }

                        return null
                      }
                    }
                  : undefined
              }
              viewSettingsHeader={<InvoicesViewSettingsHeader />}
              onSaveFilters={
                onSaveFilters
                  ? () => onSaveFilters?.(activeFilters, sortBy)
                  : undefined
              }
              onUpdateSavedFilters={
                onUpdateSavedFilters
                  ? () => onUpdateSavedFilters?.(activeFilters, sortBy)
                  : undefined
              }
              requiredColumnIds={requiredColumnIds}
              lockedControls={lockedControls}
              {...additionalToolbarComponentsV3}
            />
          </>
        )}
      </CurrentUserId>
      <BulkActionsInFlight
        selectionCount={selectedInvoices.length}
        onChangeActiveBulkActionCount={handleChangeActiveBulkActionCount}
        onChangeHasBulkActions={handleChangeHasBulkActions}
        config={invoiceBulkActionConfig}
      />
    </Box>
  )
}
